// The sales page's state lives in the URL, so any view of it is a link.
//
// Keys are prefixed `sl_` so no other route's search can mean something else
// by the same key: TanStack Router intersects every route's search type.

import { z } from "zod";
import { isCalendarDate } from "../../format/calendarDate.js";
import { BOARD_METRICS, type BoardMetric, type BoardSplit } from "./salesBoard.js";
import {
  DEFAULT_MEASURE,
  samePeriod,
  thisMonth,
  type Basis,
  type Measure,
  type Period,
} from "./salesModel.js";

const IsoDay = z.string().refine(isCalendarDate);

export type SideView = "partners" | "deals";
export const SIDE_VIEWS: readonly SideView[] = ["partners", "deals"];

const SalesSearchSchema = z.object({
  sl_from: IsoDay.optional().catch(undefined),
  sl_to: IsoDay.optional().catch(undefined),
  sl_basis: z.enum(["gross", "our_share"]).optional().catch(undefined),
  sl_weighted: z.enum(["yes", "no"]).optional().catch(undefined),
  sl_chart: z
    .enum(BOARD_METRICS as [BoardMetric, ...BoardMetric[]])
    .optional()
    .catch(undefined),
  sl_side: z
    .enum(SIDE_VIEWS as [SideView, ...SideView[]])
    .optional()
    .catch(undefined),
  sl_by: z.enum(["partner", "deal"]).optional().catch(undefined),
  /** The partner or deal picked out in the chart: its id, or "other". */
  sl_focus: z.string().min(1).max(64).optional().catch(undefined),
});

export type SalesSearch = z.infer<typeof SalesSearchSchema>;

export function validateSalesSearch(raw: Record<string, unknown>): SalesSearch {
  const parsed = SalesSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

export interface SalesState {
  readonly period: Period;
  readonly measure: Measure;
  readonly chart: BoardMetric;
  readonly split: BoardSplit;
  readonly side: SideView;
  readonly focus: string | null;
}

/** Everything but the period, which defaults to the month `today` is in. */
export const DEFAULT_SALES_STATE: Omit<SalesState, "period"> = {
  measure: DEFAULT_MEASURE,
  chart: "arr",
  split: "partner",
  side: "partners",
  focus: null,
};

export function searchToSalesState(search: SalesSearch, today: string): SalesState {
  return {
    period:
      search.sl_from === undefined
        ? thisMonth(today)
        : { from: search.sl_from, to: search.sl_to ?? null },
    measure: {
      basis: (search.sl_basis ?? DEFAULT_MEASURE.basis) as Basis,
      weighted:
        search.sl_weighted === undefined ? DEFAULT_MEASURE.weighted : search.sl_weighted === "yes",
    },
    chart: search.sl_chart ?? DEFAULT_SALES_STATE.chart,
    split: search.sl_by ?? DEFAULT_SALES_STATE.split,
    side: search.sl_side ?? DEFAULT_SALES_STATE.side,
    focus: search.sl_focus ?? null,
  };
}

/** Only what differs from the default goes in the URL, so the plain page has a plain link. */
export function salesStateToSearch(state: SalesState, today: string): SalesSearch {
  const d = DEFAULT_SALES_STATE;
  return {
    ...(samePeriod(state.period, thisMonth(today))
      ? {}
      : {
          sl_from: state.period.from,
          ...(state.period.to === null ? {} : { sl_to: state.period.to }),
        }),
    ...(state.measure.basis === d.measure.basis ? {} : { sl_basis: state.measure.basis }),
    ...(state.measure.weighted === d.measure.weighted
      ? {}
      : { sl_weighted: state.measure.weighted ? "yes" : "no" }),
    ...(state.chart === d.chart ? {} : { sl_chart: state.chart }),
    ...(state.split === d.split ? {} : { sl_by: state.split }),
    ...(state.side === d.side ? {} : { sl_side: state.side }),
    ...(state.focus === null ? {} : { sl_focus: state.focus }),
  };
}
